import { createRoute, Outlet } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { rootRoute } from '../__root'
import { Sidebar } from '@/components/sidebar'
import { Navbar } from '@/components/navbar'
import { ProjectLoadError, ProjectProvider, useProject } from '@/lib/project-context'
import { PresenceProvider, useProjectEvent } from '@/lib/collab/presence-context'
import { PageSubtitleProvider } from '@/lib/page-title-context'
import { useAuthState } from '@/lib/auth-state'
import { forgetLastProjectId, writeLastProjectId } from '@/lib/last-project'
import { orpc } from '@/lib/orpc-client'
import { JamProjectScopeSync } from '@/lib/jam/jam-loader'
import { MeetingSessionProvider } from '@/lib/meetings/meeting-session-context'
import { OngoingMeetingCard } from '@/components/meet/ongoing-meeting-card'
import { LeaveProjectDialog } from '@/components/meet/leave-project-dialog'
import { BubbleMotionProvider } from '@/components/chat/bubble-motion-provider'
import { AppErrorPage } from '@/components/route-error'
import { SettingsDialog } from '@/components/settings/settings-dialog'
import { validateSettingsSearch } from '@/lib/settings-dialog'

export const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$projectId',
  component: ProjectLayout,
  // `?settings=<section>` opens the settings dialog over any project page (board h39z).
  validateSearch: validateSettingsSearch,
  // The layout itself failed (sidebar, navbar, providers): no chrome to keep.
  errorComponent: AppErrorPage,
})

function ProjectLayout() {
  const { projectId } = projectRoute.useParams()
  const { t } = useTranslation()

  return (
    <ProjectProvider
      projectId={projectId}
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="text-sm text-muted-foreground">{t('project.loading')}</div>
        </div>
      }
      errorFallback={() => <ForgetUnavailableProject projectId={projectId} />}
    >
      <RememberLastProject projectId={projectId} />
      <JamProjectScopeSync />
      <PresenceProvider projectId={projectId}>
        <ReloadProjectOnChange />
        {/* The meeting session (board fqw7) belongs to the project, not to the
            /meet route: keyed by project so switching projects can never carry
            a media session across. */}
        <MeetingSessionProvider key={projectId} projectId={projectId}>
          <PageSubtitleProvider>
            {/* The HUB bubble's entrance/exit outlives the route that shows it (board zsy4). */}
            <BubbleMotionProvider>
              <div className="flex h-screen bg-background">
                <Sidebar className="hidden md:flex" />
                {/* The navbar overlays the page (translucent + blur). Views sit below
                  it by default; a view that should scroll underneath it opts in
                  with `-mt-(--topbar-h)` and its own top padding. */}
                <main className="relative flex flex-1 flex-col overflow-hidden">
                  <Navbar />
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-(--topbar-h)">
                    <Outlet />
                  </div>
                  <OngoingMeetingCard />
                </main>
              </div>
              <SettingsDialog />
            </BubbleMotionProvider>
          </PageSubtitleProvider>
          <LeaveProjectDialog />
        </MeetingSessionProvider>
      </PresenceProvider>
    </ProjectProvider>
  )
}

/**
 * Persist the project as the user's last visited one — locally (instant, for
 * the app root and /settings) and on the profile (follows the user between
 * devices). Rendered inside ProjectProvider so it only runs once the project
 * actually loaded, i.e. never for an id the user cannot open, and only when
 * the id changes — not per render.
 */
function RememberLastProject({ projectId }: { projectId: string }) {
  const { me } = useAuthState()
  const userId = me?.user.id

  useEffect(() => {
    if (!userId) return
    writeLastProjectId(userId, projectId)
    orpc.me.setLastProject({ projectId }).catch(() => {
      // Best effort: the local copy still steers the next redirect.
    })
  }, [userId, projectId])

  return null
}

/**
 * The project's own settings changed elsewhere (the agent saved the
 * onboarding, another tab edited Settings › Project): refetch, so the chat
 * landing card and the settings form reflect it. Needs the hub socket, hence
 * inside PresenceProvider.
 */
function ReloadProjectOnChange() {
  const { reload } = useProject()
  useProjectEvent(['project:changed'], () => reload())
  return null
}

/**
 * The project could not be loaded (deleted, access revoked): drop the local
 * hint so "go home" does not bounce straight back here, then show the usual
 * not-found screen.
 */
function ForgetUnavailableProject({ projectId }: { projectId: string }) {
  const { me } = useAuthState()
  const userId = me?.user.id

  useEffect(() => {
    if (userId) forgetLastProjectId(userId, projectId)
  }, [userId, projectId])

  return <ProjectLoadError />
}
